'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type * as React from 'react'

import { cn } from '@/lib/utils'

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return <TooltipPrimitive.Provider data-slot='tooltip-provider' delayDuration={delayDuration} {...props} />
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
	return (
		<TooltipProvider>
			<TooltipPrimitive.Root data-slot='tooltip' disableHoverableContent={true} {...props} />
		</TooltipProvider>
	)
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
	return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />
}

function TooltipContent({
	className,
	// Keep content + arrow clear of the trigger: at 0 the content spawns under a cursor
	// entering from the tooltip side, firing pointerleave on the trigger and closing it.
	sideOffset = 6,
	hideArrow = false,
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & { hideArrow?: boolean }) {
	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content
				data-slot='tooltip-content'
				sideOffset={sideOffset}
				className={cn(
					'bg-tooltip text-tooltip-foreground rounded-md shadow-tooltip px-3 py-1.5 text-body text-balance',
					// No slide-in-from-* here: the slide starts the content 8px toward the trigger,
					// overlapping a cursor entering from the tooltip side and closing it mid-animation.
					'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
					'z-[500] w-fit max-w-xs break-words origin-(--radix-tooltip-content-transform-origin)',
					className,
				)}
				{...props}>
				{children}
				{!hideArrow && (
					<TooltipPrimitive.Arrow className='bg-tooltip fill-tooltip z-[500] size-2.5 translate-y-[calc(-50%-0.125rem)] rotate-45 rounded-none' />
				)}
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	)
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
